import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CreditCard, Save } from 'lucide-react';
import { api } from '../context/AuthContext';
import './Settings.css';

const PaymentMethod = () => {
  const navigate = useNavigate();
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ account_holder_name: '', bsb: '', account_number: '' });

  useEffect(() => {
    (async () => {
      try {
        const data = await api('/api/users/me/payout');
        setPayout(data);
        setForm({
          account_holder_name: data.account_holder_name || '',
          bsb: data.bsb || '',
          account_number: '', // never pre-fill full account number
        });
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    if (form.account_holder_name.trim().length < 2) return toast.error('Please enter the account holder name.');
    if (!/^\d{3}-?\d{3}$/.test(form.bsb.trim())) return toast.error('BSB must be 6 digits, e.g. 062-000.');
    if (!/^\d{4,12}$/.test(form.account_number.trim())) return toast.error('Account number must be 4–12 digits.');
    setSaving(true);
    try {
      await api('/api/users/me/payout', { method: 'POST', body: JSON.stringify(form) });
      const fresh = await api('/api/users/me/payout');
      setPayout(fresh);
      setForm(f => ({ ...f, account_number: '' }));
      toast.success('Cashout details saved.');
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 70px)', background: '#0F0F0F', color: '#fff', overflowY: 'auto' }} data-testid="payment-method-page">
      <div style={{ display: 'flex', alignItems: 'center', padding: '20px 20px', borderBottom: '1px solid #222' }}>
        <button
          onClick={() => navigate('/settings')}
          style={{ background: '#222', border: 'none', width: 40, height: 40, borderRadius: 10, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12 }}
          data-testid="payment-back-btn"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 700, margin: 0 }}>Payment Method</h1>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading…</div>
      ) : (
        <div style={{ padding: 20 }}>
          <div style={{ background: '#1A1A1A', borderRadius: 14, padding: 20, marginBottom: 24 }}>
            <p style={{ color: '#9CA3AF', fontSize: 14, margin: 0 }}>Current balance held</p>
            <p style={{ color: '#E07A5F', fontSize: 32, fontWeight: 800, margin: '4px 0', fontFamily: 'Fraunces, serif' }} data-testid="balance-held">
              ${(payout?.balance_held || 0).toFixed(2)}
            </p>
            <p style={{ color: '#9CA3AF', fontSize: 13, margin: 0 }}>Paid out weekly to your nominated account below.</p>
          </div>

          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#9CA3AF', letterSpacing: 0.5, marginBottom: 6 }}>ACCOUNT HOLDER NAME</label>
          <input
            value={form.account_holder_name}
            onChange={(e) => setForm(p => ({ ...p, account_holder_name: e.target.value }))}
            placeholder="e.g. Jane Smith"
            data-testid="payout-account-name"
            style={{ width: '100%', padding: '14px 16px', borderRadius: 10, border: '1px solid #2A2A2A', background: '#1A1A1A', color: '#fff', fontSize: 15, marginBottom: 20, boxSizing: 'border-box' }}
          />

          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#9CA3AF', letterSpacing: 0.5, marginBottom: 6 }}>BSB</label>
          <input
            value={form.bsb}
            onChange={(e) => setForm(p => ({ ...p, bsb: e.target.value }))}
            placeholder="062-000"
            data-testid="payout-bsb"
            style={{ width: '100%', padding: '14px 16px', borderRadius: 10, border: '1px solid #2A2A2A', background: '#1A1A1A', color: '#fff', fontSize: 15, marginBottom: 20, boxSizing: 'border-box' }}
          />

          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#9CA3AF', letterSpacing: 0.5, marginBottom: 6 }}>
            ACCOUNT NUMBER {payout?.account_number_masked && <span style={{ marginLeft: 8, color: '#4B5563' }}>(current: {payout.account_number_masked})</span>}
          </label>
          <input
            value={form.account_number}
            onChange={(e) => setForm(p => ({ ...p, account_number: e.target.value }))}
            placeholder="e.g. 12345678"
            inputMode="numeric"
            data-testid="payout-account-number"
            style={{ width: '100%', padding: '14px 16px', borderRadius: 10, border: '1px solid #2A2A2A', background: '#1A1A1A', color: '#fff', fontSize: 15, marginBottom: 24, boxSizing: 'border-box' }}
          />

          <button
            onClick={save}
            disabled={saving}
            data-testid="payout-save-btn"
            style={{ width: '100%', padding: '16px', borderRadius: 12, border: 'none', background: '#E07A5F', color: '#fff', fontWeight: 700, fontSize: 16, cursor: saving ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <Save size={18} /> {saving ? 'Saving…' : 'Save cashout details'}
          </button>

          <p style={{ color: '#6B7280', fontSize: 12, marginTop: 20, lineHeight: 1.5 }}>
            Your account number is stored encrypted at rest. Only 6th Mate admins can see full details when arranging a payout.
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentMethod;
